import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './CurriculumForm.css';

function CurriculumForm({ asTab = false }) {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  // Grades state - array of { id, name, streams: [] }
  const [grades, setGrades] = useState([]);
  const [newGradeName, setNewGradeName] = useState('');
  const [newGrades, setNewGrades] = useState(['']); // For adding multiple grades at once

  // Sections state (for dedicated sections section)
  const [selectedGradeForStream, setSelectedGradeForStream] = useState('');
  const [newStreams, setNewStreams] = useState(['']); // For adding multiple sections at once

  // Subjects state
  const [selectedGradeForSubject, setSelectedGradeForSubject] = useState('');
  const [subjects, setSubjects] = useState([]); // Array of { grade_id, subject_name }
  const [newSubjects, setNewSubjects] = useState(['']); // For adding multiple subjects at once

  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      try {
        setSchoolData(JSON.parse(storedSchoolData));
      } catch (e) {
        console.error('Error parsing school data:', e);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      // Reset form data when branch changes
      if (selectedBranchId !== undefined) {
        setNewGrades(['']);
        setNewStreams(['']);
        setNewSubjects(['']);
        setSelectedGradeForStream('');
        setSelectedGradeForSubject('');
      }
      fetchCurriculumData();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchCurriculumData = async () => {
    if (!schoolData?.id) return;

    setLoadingData(true);
    try {
      // Fetch grades
      let gradesQuery = supabase
        .from('grades')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        gradesQuery = gradesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: gradesData, error: gradesError } = await gradesQuery.order('name');

      if (gradesError) throw gradesError;

      // Fetch streams for all grades
      const gradeIds = gradesData?.map(g => g.id) || [];
      let streamsQuery = supabase
        .from('streams')
        .select('*')
        .in('grade_id', gradeIds);

      if (selectedBranchId) {
        streamsQuery = streamsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: streamsData, error: streamsError } = await streamsQuery.order('name');

      if (streamsError) throw streamsError;

      // Fetch subjects
      let subjectsQuery = supabase
        .from('subjects')
        .select('*')
        .in('grade_id', gradeIds);

      if (selectedBranchId) {
        subjectsQuery = subjectsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: subjectsData, error: subjectsError } = await subjectsQuery.order('subject_name');

      if (subjectsError) throw subjectsError;

      // Organize streams by grade
      const gradesWithStreams = (gradesData || []).map(grade => ({
        ...grade,
        streams: (streamsData || []).filter(s => s.grade_id === grade.id)
      }));

      setGrades(gradesWithStreams);
      setSubjects(subjectsData || []);

      // Find the most recent updated_at timestamp from all curriculum data
      const allTimestamps = [
        ...(gradesData || []).map(g => g.updated_at),
        ...(streamsData || []).map(s => s.updated_at),
        ...(subjectsData || []).map(s => s.updated_at)
      ].filter(Boolean);

      if (allTimestamps.length > 0) {
        const mostRecent = allTimestamps.sort((a, b) => 
          new Date(b) - new Date(a)
        )[0];
        setLastUpdated(mostRecent);
      } else {
        setLastUpdated(null);
      }
    } catch (error) {
      console.error('Error fetching curriculum data:', error);
      setMessage({ type: 'error', text: 'Failed to load curriculum data' });
    } finally {
      setLoadingData(false);
    }
  };

  // Handle adding multiple grades at once
  const handleAddGrades = async () => {
    if (!selectedBranchId) {
      setMessage({ type: 'error', text: 'Please select a branch first before adding classes.' });
      return;
    }

    const gradesToAdd = newGrades.filter(g => g.trim() !== '');
    if (gradesToAdd.length === 0) {
      setMessage({ type: 'error', text: 'Please enter at least one class name' });
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const gradesToInsert = gradesToAdd.map(gradeName => ({
        school_id: schoolData.id,
        branch_id: selectedBranchId,
        name: gradeName.trim(),
        updated_at: now
      }));

      const { data, error } = await supabase
        .from('grades')
        .insert(gradesToInsert)
        .select();

      if (error) throw error;

      setMessage({ type: 'success', text: `Successfully added ${data.length} class(es)` });
      setNewGrades(['']); // Reset input
      await fetchCurriculumData();
    } catch (error) {
      console.error('Error adding grades:', error);
      setMessage({ type: 'error', text: 'Failed to add classes' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewGradeInput = () => {
    setNewGrades([...newGrades, '']);
  };

  const handleNewGradeChange = (index, value) => {
    const updated = [...newGrades];
    updated[index] = value;
    setNewGrades(updated);
  };

  const handleRemoveNewGradeInput = (index) => {
    if (newGrades.length > 1) {
      setNewGrades(newGrades.filter((_, i) => i !== index));
    }
  };

  // Handle adding sections from dedicated sections section (stored in streams table)
  const handleAddStreamsFromSection = async () => {
    if (!selectedBranchId) {
      setMessage({ type: 'error', text: 'Please select a branch first before adding sections.' });
      return;
    }

    if (!selectedGradeForStream) {
      setMessage({ type: 'error', text: 'Please select a class first' });
      return;
    }

    const streamsToAdd = newStreams.filter(s => s.trim() !== '');
    if (streamsToAdd.length === 0) {
      setMessage({ type: 'error', text: 'Please enter at least one section name' });
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const streamsToInsert = streamsToAdd.map(streamName => ({
        grade_id: selectedGradeForStream,
        school_id: schoolData.id,
        branch_id: selectedBranchId,
        name: streamName.trim(),
        updated_at: now
      }));

      const { data, error } = await supabase
        .from('streams')
        .insert(streamsToInsert)
        .select();

      if (error) throw error;

      setMessage({ type: 'success', text: `Successfully added ${data.length} section(s)` });
      setNewStreams(['']);
      setSelectedGradeForStream('');
      await fetchCurriculumData();
    } catch (error) {
      console.error('Error adding streams:', error);
      setMessage({ type: 'error', text: 'Failed to add sections' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewStreamInput = () => {
    setNewStreams([...newStreams, '']);
  };

  const handleNewStreamChange = (index, value) => {
    const updated = [...newStreams];
    updated[index] = value;
    setNewStreams(updated);
  };

  const handleRemoveNewStreamInput = (index) => {
    if (newStreams.length > 1) {
      setNewStreams(newStreams.filter((_, i) => i !== index));
    }
  };

  // Handle adding subjects for a selected grade
  const handleAddSubjects = async () => {
    if (!selectedBranchId) {
      setMessage({ type: 'error', text: 'Please select a branch first before adding subjects.' });
      return;
    }

    if (!selectedGradeForSubject) {
      setMessage({ type: 'error', text: 'Please select a class first' });
      return;
    }

    const subjectsToAdd = newSubjects.filter(s => s.trim() !== '');
    if (subjectsToAdd.length === 0) {
      setMessage({ type: 'error', text: 'Please enter at least one subject name' });
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const subjectsToInsert = subjectsToAdd.map(subjectName => ({
        grade_id: selectedGradeForSubject,
        school_id: schoolData.id,
        branch_id: selectedBranchId,
        subject_name: subjectName.trim(),
        updated_at: now
      }));

      const { data, error } = await supabase
        .from('subjects')
        .insert(subjectsToInsert)
        .select();

      if (error) throw error;

      setMessage({ type: 'success', text: `Successfully added ${data.length} subject(s)` });
      setNewSubjects(['']);
      setSelectedGradeForSubject('');
      await fetchCurriculumData();
    } catch (error) {
      console.error('Error adding subjects:', error);
      setMessage({ type: 'error', text: 'Failed to add subjects' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewSubjectInput = () => {
    setNewSubjects([...newSubjects, '']);
  };

  const handleNewSubjectChange = (index, value) => {
    const updated = [...newSubjects];
    updated[index] = value;
    setNewSubjects(updated);
  };

  const handleRemoveNewSubjectInput = (index) => {
    if (newSubjects.length > 1) {
      setNewSubjects(newSubjects.filter((_, i) => i !== index));
    }
  };

  // Delete functions
  const handleDeleteGrade = async (gradeId) => {
    if (!window.confirm('Are you sure you want to delete this class? This will also delete all associated sections and subjects.')) {
      return;
    }

    setSaving(true);
    try {
      // Delete subjects first
      await supabase.from('subjects').delete().eq('grade_id', gradeId);
      // Delete streams
      await supabase.from('streams').delete().eq('grade_id', gradeId);
      // Delete grade
      const { error } = await supabase.from('grades').delete().eq('id', gradeId);
      if (error) throw error;

      setMessage({ type: 'success', text: 'Class deleted successfully' });
      await fetchCurriculumData();
    } catch (error) {
      console.error('Error deleting grade:', error);
      setMessage({ type: 'error', text: 'Failed to delete class' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStream = async (streamId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) {
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('streams').delete().eq('id', streamId);
      if (error) throw error;

      setMessage({ type: 'success', text: 'Section deleted successfully' });
      await fetchCurriculumData();
    } catch (error) {
      console.error('Error deleting stream:', error);
      setMessage({ type: 'error', text: 'Failed to delete section' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubject = async (subjectId) => {
    if (!window.confirm('Are you sure you want to delete this subject?')) {
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('subjects').delete().eq('id', subjectId);
      if (error) throw error;

      setMessage({ type: 'success', text: 'Subject deleted successfully' });
      await fetchCurriculumData();
    } catch (error) {
      console.error('Error deleting subject:', error);
      setMessage({ type: 'error', text: 'Failed to delete subject' });
    } finally {
      setSaving(false);
    }
  };

  // If loading and not a tab, show loading screen
  if (loading && !asTab) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // If loading and is a tab, show loading in the tab
  if (loading && asTab) {
    return (
      <div className="dashboard-content">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const formContent = (
    <div className="dashboard-content">
            {message.text && (
              <div className={`message ${message.type}`}>
                {message.text}
                <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
              </div>
            )}

            {/* Branch Selector Section */}
            <div className="dashboard-branch-row schedule-view-branch-row" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <BranchSelector
                schoolId={schoolData?.id}
                selectedBranchId={selectedBranchId}
                onBranchChange={handleBranchChange}
              />
              <div className="branch-context" style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6B7280' }}>
                {selectedBranchId 
                  ? `📍 Showing curriculum for the selected branch` 
                  : '⚠️ Please select a branch to view and manage curriculum'}
              </div>
              {lastUpdated && (
                <div style={{ 
                  marginTop: '0.75rem', 
                  padding: '8px 12px', 
                  backgroundColor: '#EFF6FF', 
                  borderRadius: '6px', 
                  fontSize: '0.85rem', 
                  color: '#1E40AF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🕒</span>
                  <span>
                    <strong>Last Updated:</strong> {new Date(lastUpdated).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Show message if no branch is selected */}
            {!selectedBranchId ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem 2rem', 
                background: '#F9FAFB', 
                borderRadius: '8px', 
                border: '1px solid #E5E7EB',
                marginBottom: '2rem'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📍</div>
                <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Select a Branch</h3>
                <p style={{ color: '#6B7280', fontSize: '1.1rem', lineHeight: '1.6' }}>
                  Please select a branch from the dropdown above to view and manage curriculum (classes, sections, and subjects) for that branch.
                </p>
              </div>
            ) : (
              <>
            {/* Classes Section */}
            <div className="curriculum-section">
              <h2>Class</h2>
              <p className="section-description">Add multiple classes at once (e.g., Class 1, Class 2)</p>
              
              <div className="form-group">
                <div className="multi-input-group">
                  {newGrades.map((grade, index) => (
                    <div key={index} className="input-with-remove">
                      <input
                        type="text"
                        value={grade}
                        onChange={(e) => handleNewGradeChange(index, e.target.value)}
                        placeholder={`Class ${index + 1} (e.g., Class 1)`}
                        className="form-input"
                      />
                      {newGrades.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveNewGradeInput(index)}
                          className="remove-btn"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="form-actions" style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleAddNewGradeInput}
                    className="btn-secondary"
                    style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '10px 20px',
                      background: '#F3F4F6',
                      color: '#374151',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + Add Another Class
                  </button>
                  <button
                    type="button"
                    onClick={handleAddGrades}
                    className="btn-primary"
                    disabled={saving}
                    style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '10px 20px',
                      background: '#FF8B3D',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {saving ? 'Saving...' : 'Add Classes'}
                  </button>
                </div>
              </div>

              {/* Display existing classes */}
              {loadingData ? (
                <p>Loading classes...</p>
              ) : grades.length > 0 ? (
                <div className="grades-list">
                  {grades.map(grade => (
                    <div key={grade.id} className="grade-card">
                      <div className="grade-header">
                        <h3>{grade.name}</h3>
                        <div className="grade-actions">
                          <button
                            type="button"
                            onClick={() => handleDeleteGrade(grade.id)}
                            className="btn-danger"
                            disabled={saving}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">No classes added yet. Add classes above.</p>
              )}
            </div>

            {/* Sections Section */}
            <div className="curriculum-section">
              <h2>Sections</h2>
              <p className="section-description">Select a class and add multiple sections for that class</p>
              
              <div className="form-group">
                <label>Select Class *</label>
                <select
                  value={selectedGradeForStream}
                  onChange={(e) => {
                    setSelectedGradeForStream(e.target.value);
                    setNewStreams(['']); // Reset streams when grade changes
                  }}
                  className="form-input"
                >
                  <option value="">-- Select Class --</option>
                  {grades.map(grade => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedGradeForStream && (
                <div className="form-group">
                  <label>Add Sections for {grades.find(g => g.id === parseInt(selectedGradeForStream))?.name}</label>
                  <div className="multi-input-group">
                    {newStreams.map((stream, index) => (
                      <div key={index} className="input-with-remove">
                        <input
                          type="text"
                          value={stream}
                          onChange={(e) => handleNewStreamChange(index, e.target.value)}
                          placeholder={`Section ${index + 1} (e.g., Section A)`}
                          className="form-input"
                        />
                        {newStreams.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveNewStreamInput(index)}
                            className="remove-btn"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="form-actions" style={{ display: 'flex', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={handleAddNewStreamInput}
                      className="btn-secondary"
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '10px 20px',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      + Add Another Section
                    </button>
                    <button
                      type="button"
                      onClick={handleAddStreamsFromSection}
                      className="btn-primary"
                      disabled={saving}
                      style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '10px 20px',
                        background: '#FF8B3D',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        opacity: saving ? 0.6 : 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {saving ? 'Saving...' : 'Add Sections'}
                    </button>
                  </div>
                </div>
              )}

              {/* Display existing sections grouped by class */}
              {loadingData ? (
                <p>Loading sections...</p>
              ) : grades.some(grade => grade.streams && grade.streams.length > 0) ? (
                <div className="streams-list" style={{ marginTop: '24px' }}>
                  {grades.map(grade => {
                    if (!grade.streams || grade.streams.length === 0) return null;
                    
                    return (
                      <div key={grade.id} className="stream-group" style={{ marginBottom: '24px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                          {grade.name.replace(/^Grade\b/i, 'Class')} Sections:
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                          {grade.streams.map(stream => (
                            <div key={stream.id} className="stream-item">
                              <div className="stream-name-horizontal">
                                {stream.name}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDeleteStream(stream.id)}
                                className="stream-delete-btn"
                                disabled={saving}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-data">No sections added yet. Select a class and add sections above.</p>
              )}
            </div>

            {/* Subjects Section */}
            <div className="curriculum-section">
              <h2>Subjects</h2>
              <p className="section-description">Select a class and add multiple subjects for that class</p>
              
              <div className="form-group">
                <label>Select Class *</label>
                <select
                  value={selectedGradeForSubject}
                  onChange={(e) => {
                    setSelectedGradeForSubject(e.target.value);
                    setNewSubjects(['']); // Reset subjects when grade changes
                  }}
                  className="form-input"
                >
                  <option value="">-- Select Class --</option>
                  {grades.map(grade => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedGradeForSubject && (
                <div className="form-group">
                  <label>Add Subjects for {grades.find(g => g.id === parseInt(selectedGradeForSubject))?.name}</label>
                  <div className="multi-input-group">
                    {newSubjects.map((subject, index) => (
                      <div key={index} className="input-with-remove">
                        <input
                          type="text"
                          value={subject}
                          onChange={(e) => handleNewSubjectChange(index, e.target.value)}
                          placeholder={`Subject ${index + 1} (e.g., Mathematics)`}
                          className="form-input"
                        />
                        {newSubjects.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveNewSubjectInput(index)}
                            className="remove-btn"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={handleAddNewSubjectInput}
                      className="btn-secondary"
                    >
                      + Add Another Subject
                    </button>
                    <button
                      type="button"
                      onClick={handleAddSubjects}
                      className="btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Add Subjects'}
                    </button>
                  </div>
                </div>
              )}

              {/* Display existing subjects grouped by grade */}
              {loadingData ? (
                <p>Loading subjects...</p>
              ) : subjects.length > 0 ? (
                <div className="subjects-list">
                  {grades.map(grade => {
                    const gradeSubjects = subjects.filter(s => s.grade_id === grade.id);
                    if (gradeSubjects.length === 0) return null;
                    
                    return (
                      <div key={grade.id} className="subject-group">
                        <h4>{grade.name} Subjects:</h4>
                        <div className="subjects-grid">
                          {gradeSubjects.map(subject => (
                            <div key={subject.id} className="subject-item">
                              <span>{subject.subject_name}</span>
                              <button
                                type="button"
                                onClick={() => handleDeleteSubject(subject.id)}
                                className="btn-danger-small"
                                disabled={saving}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-data">No subjects added yet. Select a class and add subjects above.</p>
              )}
            </div>
              </>
            )}
    </div>
  );

  // If used as a tab, just return the form content
  if (asTab) {
    return formContent;
  }

  // Otherwise, return full page with sidebar
  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content">
        <div className="content-header desktop-only">
          <h1>Curriculum Form</h1>
        </div>
        
        <div className="content-body">
          {formContent}
        </div>
      </div>
    </div>
  );
}

export default CurriculumForm;
